import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import PortalAdminLayout from "./PortalAdminLayout";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;

function SupportAgentsManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agents, setAgents] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
  });

  const checkPortalAdmin = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return false;
    }

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(""),
      );

      const decoded = JSON.parse(jsonPayload);
      const isR6 =
        decoded.role === "R6" ||
        decoded.role === "portal_admin" ||
        (Array.isArray(decoded.roles) &&
          (decoded.roles.includes("R6") ||
            decoded.roles.includes("portal_admin")));

      if (!isR6) {
        navigate("/portal-admin");
        return false;
      }

      return true;
    } catch (_error) {
      navigate("/login");
      return false;
    }
  }, [navigate]);

  const fetchAgents = useCallback(
    async (searchTerm = "") => {
      const token = localStorage.getItem("ogestyle_token");
      setLoading(true);
      setError("");

      try {
        const query = new URLSearchParams();
        if (searchTerm.trim()) {
          query.set("search", searchTerm.trim());
        }

        const url = `${API_BASE_URL}/api/admin/support-agents${
          query.toString() ? `?${query.toString()}` : ""
        }`;

        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setAgents(response.data?.supportAgents || []);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load support agents");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (checkPortalAdmin()) {
      fetchAgents();
    }
  }, [checkPortalAdmin, fetchAgents]);

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!form.email.trim()) {
      setError("Email is required.");
      return;
    }

    const token = localStorage.getItem("ogestyle_token");
    setSaving(true);

    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/admin/support-agents`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setMessage(response.data?.message || "Support agent updated.");
      setForm({ email: "", firstName: "", lastName: "", phone: "" });
      await fetchAgents(search);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to create or assign support agent.",
      );
    } finally {
      setSaving(false);
    }
  };

  const removeSupportAgent = async (userId) => {
    const token = localStorage.getItem("ogestyle_token");
    setError("");
    setMessage("");

    try {
      await axios.delete(`${API_BASE_URL}/api/admin/support-agents/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage("Support agent role removed.");
      await fetchAgents(search);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to remove support agent role.");
    }
  };

  return (
    <PortalAdminLayout>
      <div style={{ padding: "24px", maxWidth: "1100px", margin: "0 auto" }}>
        <h1 style={{ marginTop: 0, marginBottom: "8px" }}>Support Agents (R8)</h1>
        <p style={{ marginTop: 0, color: "#64748b", marginBottom: "20px" }}>
          Dedicated R6 workflow for support-agent onboarding and R8 assignment lifecycle.
        </p>

        {error && (
          <div style={{ marginBottom: "16px", padding: "12px", borderRadius: "8px", background: "#fee2e2", border: "1px solid #fecaca", color: "#991b1b" }}>
            {error}
          </div>
        )}
        {message && (
          <div style={{ marginBottom: "16px", padding: "12px", borderRadius: "8px", background: "#ecfdf5", border: "1px solid #bbf7d0", color: "#166534" }}>
            {message}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px" }}>
          <form onSubmit={onSubmit} style={{ border: "1px solid #e2e8f0", borderRadius: "10px", padding: "16px", background: "#f8fafc" }}>
            <h2 style={{ marginTop: 0, fontSize: "18px" }}>Add or Assign Support Agent</h2>
            <p style={{ color: "#64748b", fontSize: "13px" }}>
              Existing user: provide email only. New user: include first name and last name. Invite email lets them create their own password.
            </p>

            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>Email</label>
            <input
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              type="email"
              required
              style={{ width: "100%", marginBottom: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", padding: "9px" }}
            />

            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>First Name (new user)</label>
            <input
              value={form.firstName}
              onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
              type="text"
              style={{ width: "100%", marginBottom: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", padding: "9px" }}
            />

            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>Last Name (new user)</label>
            <input
              value={form.lastName}
              onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
              type="text"
              style={{ width: "100%", marginBottom: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", padding: "9px" }}
            />

            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>Phone (optional)</label>
            <input
              value={form.phone}
              onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
              type="text"
              style={{ width: "100%", marginBottom: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", padding: "9px" }}
            />

            <button
              type="submit"
              disabled={saving}
              style={{ background: "#0f766e", color: "white", border: "none", borderRadius: "8px", padding: "10px 14px", fontWeight: 600, cursor: "pointer" }}
            >
              {saving ? "Saving..." : "Add / Assign Support Agent"}
            </button>
          </form>

          <div style={{ border: "1px solid #e2e8f0", borderRadius: "10px", padding: "16px", background: "#ffffff" }}>
            <h2 style={{ marginTop: 0, fontSize: "18px" }}>Current Support Agents</h2>

            <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email"
                style={{ flex: 1, border: "1px solid #cbd5e1", borderRadius: "8px", padding: "9px" }}
              />
              <button
                type="button"
                onClick={() => fetchAgents(search)}
                style={{ border: "1px solid #cbd5e1", background: "#f8fafc", borderRadius: "8px", padding: "9px 12px", cursor: "pointer" }}
              >
                Search
              </button>
            </div>

            {loading ? (
              <div style={{ color: "#64748b" }}>Loading support agents...</div>
            ) : agents.length === 0 ? (
              <div style={{ color: "#64748b" }}>No support agents found.</div>
            ) : (
              <div style={{ display: "grid", gap: "10px", maxHeight: "470px", overflow: "auto" }}>
                {agents.map((agent) => {
                  const fullName =
                    `${agent.firstName || ""} ${agent.lastName || ""}`.trim() ||
                    agent.name ||
                    "Unknown";

                  return (
                    <div key={agent._id} style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px" }}>
                      <div style={{ fontWeight: 600 }}>{fullName}</div>
                      <div style={{ fontSize: "13px", color: "#475569" }}>{agent.email}</div>
                      <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
                        Created: {agent.createdAt ? new Date(agent.createdAt).toLocaleString() : "N/A"}
                      </div>
                      <div style={{ fontSize: "12px", color: "#64748b" }}>
                        Last login: {agent.lastLoginAt ? new Date(agent.lastLoginAt).toLocaleString() : "Never"}
                      </div>
                      <div style={{ marginTop: "8px" }}>
                        <button
                          type="button"
                          onClick={() => removeSupportAgent(agent._id)}
                          style={{ border: "1px solid #ef4444", background: "#fff", color: "#b91c1c", borderRadius: "8px", padding: "7px 10px", cursor: "pointer", fontWeight: 600, fontSize: "12px" }}
                        >
                          Remove R8 Role
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </PortalAdminLayout>
  );
}

export default SupportAgentsManagement;
